import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getChildStats, giveConsent, ChildStats } from '../services/parentApi';

export default function ChildDetail() {
  const { id } = useParams<{ id: string }>();
  const [child, setChild] = useState<ChildStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [consentLoading, setConsentLoading] = useState(false);

  useEffect(() => {
    if (id) loadStats();
  }, [id]);

  async function loadStats() {
    try {
      const data = await getChildStats(id!);
      setChild(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleGiveConsent() {
    if (!id) return;
    setConsentLoading(true);
    try {
      await giveConsent(id);
      await loadStats();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setConsentLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !child) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        {error || 'Enfant non trouvé'}
      </div>
    );
  }

  return (
    <div>
      <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm mb-4 inline-block">
        ← Retour au tableau de bord
      </Link>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <span className="text-5xl">👤</span>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{child.name}</h1>
              {child.stats.is_premium ? (
                <span className="px-2 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-full">
                  ⭐ Premium
                </span>
              ) : (
                <span className="px-2 py-1 bg-slate-100 text-slate-600 text-sm font-medium rounded-full">
                  Gratuit
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Link
              to={`/child/${id}/coach`}
              className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              🤖 Parler au coach
            </Link>
            {!child.stats.is_premium && (
              <Link
                to={`/upgrade/${id}`}
                className="px-4 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors"
              >
                ⭐ Passer Premium
              </Link>
            )}
          </div>
        </div>

        {/* Consentement parental */}
        {!child.consent_given && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-amber-900 mb-2">⚠️ Consentement parental requis</h3>
            <p className="text-sm text-amber-800 mb-4">
              Conformément au RGPD, votre consentement est nécessaire pour que votre enfant
              puisse utiliser pleinement l'application Methodix.
            </p>
            <button
              onClick={handleGiveConsent}
              disabled={consentLoading}
              className="px-4 py-2 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {consentLoading ? 'Enregistrement...' : 'Donner mon consentement'}
            </button>
          </div>
        )}

        {/* Statistiques */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-3xl font-bold text-primary-600">
              {child.stats.ai_interactions_last_7_days}
            </div>
            <div className="text-sm text-slate-600">🤖 Messages coach IA (7j)</div>
          </div>

          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-3xl font-bold text-primary-600">
              {child.stats.ai_interactions_total}
            </div>
            <div className="text-sm text-slate-600">🤖 Messages coach IA (total)</div>
          </div>

          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-3xl font-bold text-primary-600">
              {child.stats.course_sheets_count}
            </div>
            <div className="text-sm text-slate-600">📝 Fiches de cours</div>
          </div>

          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-3xl font-bold text-primary-600">
              {child.stats.quiz_count}
            </div>
            <div className="text-sm text-slate-600">❓ Quiz générés</div>
          </div>

        </div>
      </div>

      {/* Crédits */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Crédits</h2>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-3xl font-bold text-slate-900">
              {child.stats.credits_remaining}
            </div>
            <div className="text-sm text-slate-600">crédits restants ce mois</div>
          </div>

          <div className="w-48 h-4 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full"
              style={{
                width: `${Math.min(100, (child.stats.credits_remaining / (child.stats.is_premium ? 1000 : 100)) * 100)}%`,
              }}
            />
          </div>
        </div>

        <p className="mt-4 text-sm text-slate-500">
          {child.stats.is_premium
            ? 'Compte Premium : 1000 crédits/mois'
            : 'Compte gratuit : 100 crédits/mois. Passez Premium pour 1000 crédits !'}
        </p>
      </div>
    </div>
  );
}

